import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BrushCleaning, Check, ChevronLeft, ChevronRight, Download, Eye, Loader2, Printer, Thermometer } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Stepper } from "../ui/stepper";
import { useAuth } from "../../context/AuthContext";
import { withPdfPreviewParams } from "./pdfPreview";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS_OSPITI = [
  { id: "mese", label: "Seleziona mese" },
  { id: "ospiti", label: "Seleziona ospiti" },
  { id: "genera", label: "Genera documento" },
  { id: "anteprima", label: "Anteprima" },
];

const STEPS_HACCP = [
  { id: "mese", label: "Seleziona mese" },
  { id: "attrezzature", label: "Verifica attrezzature" },
  { id: "genera", label: "Genera documento" },
  { id: "anteprima", label: "Anteprima" },
];

const STEPS_PULIZIE = [
  { id: "mese", label: "Seleziona mese" },
  { id: "conferma", label: "Conferma scheda" },
  { id: "genera", label: "Genera documento" },
  { id: "anteprima", label: "Anteprima" },
];

const PRINT_TYPE_LABELS = {
  haccp: "HACCP Frigo e Congelatori",
  haccp_pulizie: "HACCP Pulizie",
  schema_alvo: "Schema Alvo",
  schema_docce: "Schema Docce",
};

export default function GeneraMeseWizard({
  open,
  onOpenChange,
  printType,
  anno,
  mese,
  meseLabel,
  token,
  onGenerated,
  apiBase = "/api/stampe-mensili",
}) {
  const { activeStruttura } = useAuth();
  const isHaccp = printType === "haccp";
  const isPulizie = printType === "haccp_pulizie";
  const STEPS = isHaccp ? STEPS_HACCP : isPulizie ? STEPS_PULIZIE : STEPS_OSPITI;
  const [step, setStep] = useState(0);
  const [ospiti, setOspiti] = useState([]);
  const [selected, setSelected] = useState([]);
  const [haccpConfig, setHaccpConfig] = useState({ frighi: [], congelatori: [] });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setResult(null);
      setSelected([]);
      setPreviewUrl(null);
      return;
    }
    if (!token) return;
    if (isHaccp) {
      axios.get(`${API_URL}${apiBase}/config/haccp`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setHaccpConfig({ frighi: r.data?.frighi || [], congelatori: r.data?.congelatori || [] }))
        .catch(() => toast.error("Errore caricamento configurazione HACCP"));
    } else if (!isPulizie) {
      axios.get(`${API_URL}${apiBase}/ospiti`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          const items = r.data.items || [];
          setOspiti(items);
          setSelected(items.map((o) => o.id));
        })
        .catch(() => toast.error("Errore caricamento ospiti"));
    }
  }, [open, token, activeStruttura?.id, isHaccp, isPulizie, apiBase]);

  const toggleOspite = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const nAttrezzature = (haccpConfig.frighi?.length || 0) + (haccpConfig.congelatori?.length || 0);

  const genera = async () => {
    setLoading(true);
    setStep(2);
    try {
      const { data } = await axios.post(
        `${API_URL}${apiBase}/${printType}/genera`,
        { anno, mese, ospiti_ids: (isHaccp || isPulizie) ? [] : selected },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(data);
      setStep(3);
      toast.success("Documento generato con successo!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore generazione");
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviewBlob = useCallback(async () => {
    if (!result || !token) return;
    try {
      const { data } = await axios.get(
        `${API_URL}${apiBase}/${printType}/file/${result.file_id}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      );
      setPreviewUrl(URL.createObjectURL(data));
    } catch {
      toast.error("Impossibile caricare l'anteprima");
    }
  }, [result, token, printType, apiBase]);

  useEffect(() => {
    if (step === 3 && result) fetchPreviewBlob();
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, result]);

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = result?.file_name || "documento.pdf";
    a.click();
  };

  const titleLabel = PRINT_TYPE_LABELS[printType] || printType?.replace(/_/g, " ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${step === 3 ? "max-w-5xl" : "max-w-3xl"} max-h-[92vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle>Genera {titleLabel} — {meseLabel}</DialogTitle>
        </DialogHeader>

        <Stepper steps={STEPS} currentStep={step} onStepChange={(i) => i < step && setStep(i)} />

        <div className="mt-6 min-h-[240px]">
          {step === 0 && (
            <div className="rounded-xl border bg-slate-50 p-6 text-center">
              <p className="text-lg font-semibold text-slate-800">{meseLabel}</p>
              <p className="mt-2 text-sm text-slate-500">Conferma il mese per cui generare il documento.</p>
            </div>
          )}

          {step === 1 && isHaccp && (
            <div className="space-y-3">
              {nAttrezzature === 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
                  <Thermometer className="mx-auto h-8 w-8 text-amber-500" />
                  <p className="mt-3 text-sm font-medium text-amber-800">Nessuna attrezzatura configurata</p>
                  <p className="mt-1 text-xs text-amber-700">
                    Chiudi questa finestra e usa “Impostazioni Stampe” per aggiungere frigo e congelatori prima di generare il registro.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500">Il registro verrà generato con una scheda per ogni attrezzatura configurata:</p>
                  <div className="max-h-56 space-y-2 overflow-y-auto">
                    {haccpConfig.frighi.map((f, i) => (
                      <div key={`fr-${i}`} className="flex items-center justify-between rounded-lg border p-3 text-sm">
                        <span className="font-medium text-slate-800">Frigo — {f.nome || f.ubicazione || `#${i + 1}`}</span>
                        <span className="text-xs text-slate-400">{f.ubicazione} · {f.temperatura_attesa || "4°C"}</span>
                      </div>
                    ))}
                    {haccpConfig.congelatori.map((c, i) => (
                      <div key={`cg-${i}`} className="flex items-center justify-between rounded-lg border p-3 text-sm">
                        <span className="font-medium text-slate-800">Congelatore — {c.nome || c.ubicazione || `#${i + 1}`}</span>
                        <span className="text-xs text-slate-400">{c.ubicazione} · {c.temperatura_attesa || "-18°C"}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 1 && isPulizie && (
            <div className="rounded-xl border bg-teal-50/60 p-6 text-center">
              <BrushCleaning className="mx-auto h-8 w-8 text-teal-600" />
              <p className="mt-3 text-sm font-medium text-slate-800">
                Scheda di esecuzione e verifica pulizia e sanificazione
              </p>
              <p className="mt-2 text-xs text-slate-600">
                Verrà generata una scheda settimanale (lunedì–domenica) per ogni settimana di{" "}
                <strong>{meseLabel}</strong>, con caselle SI/NO per le aree: magazzino, somministrazione,
                lavorazione, servizi igienici/spogliatoi, dotazioni frigorifere, utensili e attrezzature.
              </p>
            </div>
          )}

          {step === 1 && !isHaccp && !isPulizie && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              <div className="mb-2 flex items-center gap-2">
                <Checkbox
                  checked={selected.length === ospiti.length}
                  onCheckedChange={(c) => setSelected(c ? ospiti.map((o) => o.id) : [])}
                  id="all-ospiti"
                />
                <Label htmlFor="all-ospiti">Seleziona tutti ({ospiti.length})</Label>
              </div>
              {ospiti.map((o) => (
                <label key={o.id} className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-slate-50">
                  <Checkbox checked={selected.includes(o.id)} onCheckedChange={() => toggleOspite(o.id)} />
                  <span className="text-sm">{o.cognome} {o.nome} {o.camera ? `— ${o.camera}` : ""}</span>
                  <span className="ml-auto text-xs text-slate-400">{o.stato}</span>
                </label>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col items-center justify-center py-12">
              {loading ? (
                <>
                  <Loader2 className="h-10 w-10 animate-spin text-[#00A86B]" />
                  <p className="mt-4 text-sm text-slate-600">Generazione in corso…</p>
                </>
              ) : result ? (
                <>
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                    <Check className="h-7 w-7 text-emerald-600" />
                  </div>
                  <p className="mt-4 font-semibold text-slate-800">Documento generato con successo!</p>
                </>
              ) : null}
            </div>
          )}

          {step === 3 && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 rounded-xl border bg-emerald-50 p-4">
                <Check className="h-6 w-6 text-emerald-600" />
                <div>
                  <p className="font-medium text-slate-800">{result.file_name}</p>
                  <p className="text-xs text-slate-500">{result.file_size_label} · {new Date(result.generated_at).toLocaleString("it-IT")}</p>
                </div>
              </div>
              <div className="overflow-hidden rounded-lg border bg-slate-100" style={{ height: "60vh", minHeight: 420 }}>
                {previewUrl ? (
                  <iframe
                    src={withPdfPreviewParams(previewUrl)}
                    title="Anteprima"
                    className="h-full w-full"
                    style={{ border: "none" }}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-slate-500">Caricamento anteprima…</div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          {step === 0 && <Button onClick={() => setStep(1)}>Continua<ChevronRight className="ml-1 h-4 w-4" /></Button>}
          {step === 1 && isHaccp && (
            <>
              <Button variant="outline" onClick={() => setStep(0)}><ChevronLeft className="mr-1 h-4 w-4" />Indietro</Button>
              <Button onClick={genera} disabled={nAttrezzature === 0 || loading}>Genera documento</Button>
            </>
          )}
          {step === 1 && isPulizie && (
            <>
              <Button variant="outline" onClick={() => setStep(0)}><ChevronLeft className="mr-1 h-4 w-4" />Indietro</Button>
              <Button onClick={genera} disabled={loading}>Genera documento</Button>
            </>
          )}
          {step === 1 && !isHaccp && !isPulizie && (
            <>
              <Button variant="outline" onClick={() => setStep(0)}><ChevronLeft className="mr-1 h-4 w-4" />Indietro</Button>
              <Button onClick={genera} disabled={selected.length === 0 || loading}>Genera documento</Button>
            </>
          )}
          {step === 3 && result && (
            <>
              <Button variant="outline" onClick={() => onGenerated({ ...result, print_type: printType, anno, mese })}>
                <Eye className="mr-2 h-4 w-4" />Anteprima
              </Button>
              <Button variant="outline" onClick={handleDownload} disabled={!previewUrl}>
                <Download className="mr-2 h-4 w-4" />Scarica PDF
              </Button>
              <Button className="bg-[#00A86B] hover:bg-[#00915d]" onClick={() => { onGenerated({ ...result, print_type: printType, anno, mese }); onOpenChange(false); }}>
                <Printer className="mr-2 h-4 w-4" />Stampa
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
